import clsx from "clsx";

import { View, Text, Button } from "./base";
import { Icon } from "../components/base/Icon";
import { useEffect, useRef, useState } from "react";
import { formatTime } from "../utils/strings";

export type AudioDownloadChoice = {
  label: string;
  onClick: () => void | Promise<void>;
};

type AudioPlayerProps = {
  audioUrl?: string;
  className?: string;
  errorMessage?: string;
  filename?: string;
  downloadChoices?: AudioDownloadChoice[];
};

export const AudioPlayer = ({ audioUrl, filename, className, errorMessage = "Error loading audio", downloadChoices }: AudioPlayerProps) => {
  const audioRef = useRef<HTMLAudioElement>();
  const downloadMenuRef = useRef<HTMLDivElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAudioLoaded, setIsAudioLoaded] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);

  const onClickPlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const onTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const onLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      setIsAudioLoaded(true);
    }
  };

  const onClickProgress = (e: React.MouseEvent<HTMLDivElement>) => {
    if (audioRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = x / rect.width;
      const newTime = percentage * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const onClickDownload = async () => {
    if (!audioUrl) return;

    // Use fetch to get the audio blob, then trigger download
    try {
      const response = await fetch(audioUrl, {
        credentials: "include",
      });

      const contentType = response.headers.get("content-type");
      if (contentType?.includes("audio/wav")) {
        filename = filename?.replace(".mp3", ".wav");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = filename || `audio_${new Date().toISOString()}.mp3`;

      document.body.appendChild(link);

      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading audio:", error);
    }
  };

  useEffect(() => {
    if (!isDownloadMenuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target as Node)) {
        setIsDownloadMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDownloadMenuOpen]);

  useEffect(() => {
    if (audioUrl) {
      setIsError(false);

      audioRef.current = new Audio(audioUrl);

      audioRef.current.addEventListener("timeupdate", onTimeUpdate);
      audioRef.current.addEventListener("loadedmetadata", onLoadedMetadata);
      audioRef.current.addEventListener("ended", () => setIsPlaying(false));
      audioRef.current.addEventListener("pause", () => setIsPlaying(false));

      audioRef.current.addEventListener("error", () => {
        setIsPlaying(false);
        setIsError(true);
      });
    } else {
      setIsAudioLoaded(false);
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeEventListener("timeupdate", onTimeUpdate);
        audioRef.current.removeEventListener("loadedmetadata", onLoadedMetadata);
        audioRef.current = undefined;
      }
    };
  }, [audioUrl]);

  return (
    <View
      className={clsx(
        "flex items-center justify-center gap-4",
        "bg-white dark:bg-gray-800",
        "border border-gray-200 dark:border-gray-700",
        "rounded-xl px-4 py-2 w-full md:w-2/3",
        "transition-all duration-300 shadow-sm",
        isAudioLoaded ? "translate-x-0 opacity-100" : "-translate-x-full opacity-0",
        className,
      )}
    >
      {isError ? (
        <Text
          variant="muted"
          className="m-1.5"
        >
          {errorMessage}
        </Text>
      ) : (
        <>
          <Button
            onClick={onClickPlay}
            variant="ghost"
            className="!w-8 !h-8 !p-0 !shadow-none hover:bg-gray-100 dark:hover:bg-gray-700 !rounded-full"
          >
            <Icon
              name={isPlaying ? "pause" : "play"}
              size={24}
              className="text-brand dark:text-brand-light"
            />
          </Button>
          <View className="flex-1 pt-2">
            <div
              className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              onClick={onClickProgress}
            >
              <View
                className="h-full bg-brand dark:bg-brand-light rounded-full transition-all duration-300"
                style={{ width: `${(currentTime / duration) * 100}%` }}
              />
            </div>
            <View className="flex justify-between mt-1">
              <Text
                variant="muted"
                className="text-xs"
              >
                {formatTime(currentTime)}
              </Text>
              <Text
                variant="muted"
                className="text-xs"
              >
                {formatTime(duration)}
              </Text>
            </View>
          </View>
          {downloadChoices && downloadChoices.length > 0 ? (
            <View
              className="relative"
              ref={downloadMenuRef}
            >
              <Button
                onClick={() => setIsDownloadMenuOpen((prev) => !prev)}
                variant="ghost"
                className="!w-8 !h-8 !p-0 !shadow-none hover:bg-gray-100 dark:hover:bg-gray-700 !rounded-full"
              >
                <Icon
                  name="download"
                  size={18}
                  className="text-brand dark:text-brand-light"
                />
              </Button>
              {isDownloadMenuOpen && (
                <View className="absolute z-10 right-0 mt-2 min-w-[140px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden">
                  {downloadChoices.map((choice) => (
                    <View
                      key={choice.label}
                      className="px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                      onClick={async () => {
                        setIsDownloadMenuOpen(false);
                        await choice.onClick();
                      }}
                    >
                      {choice.label}
                    </View>
                  ))}
                </View>
              )}
            </View>
          ) : (
            <Button
              onClick={onClickDownload}
              variant="ghost"
              className="!w-8 !h-8 !p-0 !shadow-none hover:bg-gray-100 dark:hover:bg-gray-700 !rounded-full"
            >
              <Icon
                name="download"
                size={18}
                className="text-brand dark:text-brand-light"
              />
            </Button>
          )}
        </>
      )}
    </View>
  );
};
